import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Block,
  EmptyCircle,
  ErrorNote,
  Loading,
  SageCheck,
  Screen,
} from '../components/primitives';
import { coaching, type AlignmentTodo } from '../lib/api';
import { qk, useAlignmentTodos } from '../lib/queries';

/**
 * The day's alignment to-dos, ported from the prototype's law-of-attraction
 * tab: seven small practices drawn for today and ticked off.
 *
 * These are not the member's actions. Their actions are their real plan and
 * they carry the Success Score; ticking a practice here changes nothing on
 * that score, and the screen says so rather than letting a member believe
 * they have worked their plan by drinking a glass of water.
 */
export function AlignmentTodosPage() {
  const qc = useQueryClient();
  const list = useAlignmentTodos();

  const toggle = useMutation({
    mutationFn: (todo: AlignmentTodo) => coaching.setAlignmentTodo(todo.id, !todo.done),
    onMutate: async (todo) => {
      await qc.cancelQueries({ queryKey: qk.alignmentTodos });
      const previous = qc.getQueryData<{ day: string; todos: AlignmentTodo[] }>(qk.alignmentTodos);
      qc.setQueryData<{ day: string; todos: AlignmentTodo[] }>(qk.alignmentTodos, (current) =>
        current
          ? {
              ...current,
              todos: current.todos.map((t) => (t.id === todo.id ? { ...t, done: !t.done } : t)),
            }
          : current,
      );
      return { previous };
    },
    onError: (_err, _todo, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.alignmentTodos, ctx.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.alignmentTodos }),
  });

  if (list.isPending) return <Loading label="Drawing today's list" />;
  if (list.isError) return <ErrorNote error={list.error} retry={() => void list.refetch()} />;

  const { todos } = list.data;
  const done = todos.filter((t) => t.done).length;

  return (
    <Screen
      back="/manifest"
      eyebrow="Today's alignment"
      title={done === todos.length ? 'All seven done' : `${done} of ${todos.length} done`}
      subtitle="Seven small things for today. None of them takes long, and none of them needs anything you do not already have."
    >
      <ul>
        {todos.map((todo) => (
          <li key={todo.id}>
            <button
              type="button"
              aria-pressed={todo.done}
              onClick={() => toggle.mutate(todo)}
              className="flex w-full items-start gap-3.5 border-b border-border px-5 py-4 text-left transition-colors hover:bg-surface"
            >
              <span className="mt-0.5 shrink-0">
                {todo.done ? <SageCheck size={20} /> : <EmptyCircle size={20} />}
              </span>
              <span
                className={
                  todo.done
                    ? 'flex-1 text-[0.95rem] leading-relaxed text-ink/45 line-through'
                    : 'flex-1 text-[0.95rem] leading-relaxed text-ink'
                }
              >
                {todo.text}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <Block eyebrow="A note">
        These are practices, not your plan. Ticking them does not move your Success Score — that
        follows the actions on your goal, which live in Achieve.
      </Block>
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        Tomorrow's seven are drawn fresh. Anything you leave unticked tonight is not held against
        you.
      </p>
    </Screen>
  );
}
